import PropTypes from 'prop-types';
import clone from "clone";

import {UILevel} from "../../../lib/oe-types";
import OEPopover from '../../views/oe-popover';

export const OESettingsControlGroupType = {
    background:                         'background',
    dev:                                'dev',
    graphics:                           'graphics',
    inputSensitivity:                   'input_sensitivity',
    language:                           'language',
    tutor:                              'tutor',
    ui:                                 'ui',
    vpGizmo:                            'vp_gizmo'
};

export const OESettingsControlType = {
    ambientOcclusion:                   'ambient_occlusion',
    backgroundColor:                    'background_color',
    compHighlightingVP:                 'comp_highlighting_vp',
    compHighlightingVPColor:            'comp_highlighting_vp_color',
    compHighlightingLabels:             'comp_highlighting_labels',
    language:                           'language',
    languageSwitch:                     'language_switch',
    rendererPreset:                     'renderer_preset',
    showComponentIDs:                   'showComponentIDs',
    touchSensitivity:                   'touch_sensitivity',
    tutorReset:                         'tutor_reset',
    uiLevel:                            'ui_level',
    vignette:                           'vignette',
    vpGizmo:                            'vp_gizmo'
};

//
export const OESettingsDefaultControlGroups = {

    background: {
        type:                       OESettingsControlGroupType.background,
        controls: [
            {type: OESettingsControlType.vignette, enabled: true},
            {type: OESettingsControlType.backgroundColor, enabled: true}
        ]
    },

    dev: {
        type:                       OESettingsControlGroupType.dev,
        devFeature:                 true,
        controls: [
            {type: OESettingsControlType.showComponentIDs, enabled: true, devFeature: true}
        ]
    },

    graphics: {
        type:                       OESettingsControlGroupType.graphics,
        controls: [
            {type: OESettingsControlType.ambientOcclusion, enabled: true},
            {type: OESettingsControlType.compHighlightingVP, enabled: true},
            {type: OESettingsControlType.compHighlightingVPColor, enabled: true},
            {type: OESettingsControlType.compHighlightingLabels, enabled: true},
            {type: OESettingsControlType.rendererPreset, enabled: true}
        ]
    },

    inputSensitivity: {
        type:                       OESettingsControlGroupType.inputSensitivity,
        controls: [
            {type: OESettingsControlType.touchSensitivity, enabled: true}
        ]
    },

    language: {
        type:                       OESettingsControlGroupType.language,
        controls: [
            {type: OESettingsControlType.language, enabled: true},
            {type: OESettingsControlType.languageSwitch, enabled: true}
        ]
    },

    tutor: {
        type:                       OESettingsControlGroupType.tutor,
        controls: [
            {type: OESettingsControlType.tutorReset, enabled: true}
        ]
    },

    uiSimple: {
        type:                       OESettingsControlGroupType.ui,
        controls: [
            {type: OESettingsControlType.uiLevel, enabled: true, data: [UILevel.simple, UILevel.std]}
        ]
    },

    uiAdvanced: {
        type:                       OESettingsControlGroupType.ui,
        controls: [
            {type: OESettingsControlType.uiLevel, enabled: true, data: [UILevel.std, UILevel.advanced]}
        ]
    },

    uiSimpleAdvanced: {
        type:                       OESettingsControlGroupType.ui,
        controls: [
            {type: OESettingsControlType.uiLevel, enabled: true, data: [UILevel.simple, UILevel.std, UILevel.advanced]}
        ]
    },

    vpGizmo: {
        type:                       OESettingsControlGroupType.vpGizmo,
        controls: [
            {type: OESettingsControlType.vpGizmo, enabled: true}
        ]
    }
};

//
export const OESettingsControllerConfigFactory = {
    buildStdConfig()    {
        return {
            enabled:                true,
            showHelpBtn:            true,
            controls: [
                clone(OESettingsDefaultControlGroups.language),
                clone(OESettingsDefaultControlGroups.background),
                clone(OESettingsDefaultControlGroups.vpGizmo),
                clone(OESettingsDefaultControlGroups.inputSensitivity),
                clone(OESettingsDefaultControlGroups.graphics),
                clone(OESettingsDefaultControlGroups.dev)
            ]
        };
    },

    buildSecondaryStdConfig()    {
        return {
            enabled:                true,
            showHelpBtn:            true,
            controls: [{
                type:               OESettingsControlGroupType.language,
                controls: [
                    {type: OESettingsControlType.language, enabled: true}
                ]
            }]
        };
    }
};

//
export const OESettingsControllerProps = {

    maskDecl: PropTypes.shape({
        masked:                     PropTypes.bool,
        licenseValid:               PropTypes.bool,
        licensingAccess:            PropTypes.func,
    })
};

OESettingsControllerProps.controllerConfig = {
    enabled:                        PropTypes.bool,
    controls: PropTypes.arrayOf(PropTypes.shape({
        type:                       PropTypes.string.isRequired,
        mask:                       OESettingsControllerProps.maskDecl,
        devFeature:                 PropTypes.bool,
        controls: PropTypes.arrayOf(PropTypes.shape({
            type:                   PropTypes.string.isRequired,
            mask:                   OESettingsControllerProps.maskDecl,
            enabled:                PropTypes.bool.isRequired,
            devFeature:             PropTypes.bool,
            data:                   PropTypes.any
        }).isRequired)
    }))
};

OESettingsControllerProps.controllerConfigDecl = PropTypes.shape(OESettingsControllerProps.controllerConfig).isRequired;

OESettingsControllerProps.controller = {
    moduleId:                       PropTypes.string,
    config:                         OESettingsControllerProps.controllerConfigDecl,
    onModelStateUpdateDriverRef:    PropTypes.func,
    devFeatures:                    PropTypes.bool
};

OESettingsControllerProps.controllerDecl = PropTypes.shape(OESettingsControllerProps.controller);

OESettingsControllerProps.popoverConfig = Object.assign({
    showHelpBtn:                    PropTypes.bool
}, OESettingsControllerProps.controllerConfig);

OESettingsControllerProps.popoverConfigDecl = PropTypes.shape(OESettingsControllerProps.popoverConfig).isRequired;

OESettingsControllerProps.popover = {
    className:                      PropTypes.string,
    moduleId:                       PropTypes.string,
    uiControllerType:               OEPopover.propTypes.uiControllerType,
    placement:                      OEPopover.propTypes.placement,
    hideArrow:                      OEPopover.propTypes.hideArrow,
    noHeader:                       OEPopover.propTypes.noHeader,
    offset:                         OEPopover.propTypes.offset,
    isOpen:                         OEPopover.propTypes.isOpen,
    icon:                           OEPopover.propTypes.icon,
    title:                          OEPopover.propTypes.title,
    titleId:                        OEPopover.propTypes.titleId,
    config:                         OESettingsControllerProps.popoverConfigDecl,
    devFeatures:                    PropTypes.bool
};

OESettingsControllerProps.popoverDecl = PropTypes.shape(OESettingsControllerProps.popover);

OESettingsControllerProps.view = {
    moduleId:                       PropTypes.string,
    isOpen:                         PropTypes.bool,
    config:                         OESettingsControllerProps.controllerConfigDecl,
    devFeatures:                    PropTypes.bool
};

OESettingsControllerProps.viewDecl = PropTypes.shape(OESettingsControllerProps.view);

OESettingsControllerProps.controllerDefaultProps = {
    moduleId:                       '',
    config:                         OESettingsControllerConfigFactory.buildStdConfig(),
    devFeatures:                    false
};

OESettingsControllerProps.popoverDefaultProps = {
    className:                      '',
    moduleId:                       '',
    placement:                      OEPopover.defaultProps.placement,
    hideArrow:                      OEPopover.defaultProps.hideArrow,
    placement:                      OEPopover.defaultProps.placement,
    noHeader:                       OEPopover.defaultProps.noHeader,
    isOpen:                         OEPopover.defaultProps.isOpen,
    config:                         OESettingsControllerConfigFactory.buildStdConfig(),
    devFeatures:                    false
};

OESettingsControllerProps.viewDefaultProps = {
    moduleId:                       '',
    isOpen:                         false,
    config:                         OESettingsControllerConfigFactory.buildSecondaryStdConfig(),
    devFeatures:                    false
};